import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Inicio', href: '/' },
  { label: 'Nosotros', href: '/nosotros' },
  { label: 'Derechos', href: '/derechos' },
  { label: 'Servicios', href: '/servicios' },
  { label: 'Artistas', href: '/artistas' },
  { label: 'Contacto', href: '/contacto' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const linkClassName = (isActive: boolean) =>
    `relative px-4 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
      isScrolled
        ? isActive
          ? 'text-primary-600'
          : 'text-foreground-700 hover:text-foreground-900'
        : isActive
          ? 'text-white'
          : 'text-white/80 hover:text-white'
    }`;

  const activeDot = (isActive: boolean) =>
    isActive ? (
      <span
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full transition-colors duration-500 ${
          isScrolled ? 'bg-primary-500' : 'bg-white'
        }`}
      />
    ) : null;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-secondary-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={
                isScrolled
                  ? 'https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/1febd219-06cf-4b84-a7ed-c9656a5b4924_compressed_logo_header_soniem_n-2.webp'
                  : 'https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/76aa933d-c1ed-427f-bd19-3cb55ca4b785_compressed_logo_header_soniem_b-3.webp'
              }
              alt="SONIEM"
              className="h-8 md:h-10 w-auto transition-all duration-500 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={linkClassName(isActive)}
                >
                  {link.label}
                  {activeDot(isActive)}
                </Link>
              );
            })}
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contacto"
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                isScrolled
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/20 text-white border border-white/30 hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              Afíliate
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}
            aria-label="Menú de navegación"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-400 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        } ${isScrolled ? 'bg-background-50' : 'bg-foreground-900/95 backdrop-blur-md'}`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {/* Logo en menú móvil */}
          <div className="px-4 py-3 mb-2">
            <img
              src={
                isScrolled
                  ? 'https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/1febd219-06cf-4b84-a7ed-c9656a5b4924_compressed_logo_header_soniem_n-2.webp'
                  : 'https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/76aa933d-c1ed-427f-bd19-3cb55ca4b785_compressed_logo_header_soniem_b-3.webp'
              }
              alt="SONIEM"
              className="h-8 w-auto"
            />
          </div>
          {navLinks.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isScrolled
                    ? isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-foreground-700 hover:bg-secondary-100'
                    : isActive
                      ? 'bg-white/10 text-white'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/contacto"
            className={`mt-2 px-4 py-3 rounded-full text-sm font-semibold text-center transition-colors whitespace-nowrap ${
              isScrolled
                ? 'bg-primary-500 text-white'
                : 'bg-white/20 text-white'
            }`}
          >
            Afíliate
          </Link>
        </div>
      </div>
    </nav>
  );
}